/**
 * The lines of a bid, each one explained when tapped (owner, Sep 19 2026: "an option to expand something that
 * shows all the info about it").
 *
 * The words come from the server (services/bidPage.lineInfo): what a mowing visit includes, what the bed weed
 * spray is, the lawn plan's whole year. A line the server has nothing to say about is just a line.
 * Self-contained: the page hands it the lines and the info and it draws them.
 */
import React, { useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import BidPlanYear, { PlanPicture } from './BidPlanYear';

// `mowed` (on a plan): whether we mow the yard, so its year is not said to ride a mowing visit when we do not.
export type LineInfo = { kind: string; title: string; lead?: string; points?: string[]; plan?: PlanPicture; mowed?: boolean };
// rhythm_words: a line billed per month on its own rhythm (server: services/monthlyLine) — "trimmed every 3 months" under "$100/mo".
type Line = { id: number; description: string; quantity: number; unit_price: number; total: number; unit?: string | null; cadence?: string | null; rhythm_words?: string | null };

const money = (n: number) => { const v = Math.round(Number(n) * 100) / 100; return `${v < 0 ? '−' : ''}$${Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: v % 1 ? 2 : 0, maximumFractionDigits: 2 })}`; };
// A quarterly trim is priced per visit but comes four times a year — "$500/visit" read as every visit.
// A line priced per month is "/mo" whatever its rhythm (a contract's quarterly irrigation check is $83 a month, not a quarter).
const per = (li: Line) => (li.unit === 'month' ? '/mo' : li.cadence === 'quarterly' ? ' a quarter' : li.cadence === 'yearly' ? '/yr'
  : li.cadence === 'one_time' ? '' : li.unit === 'visit' ? '/visit' : '');
const fmtQty = (q: number) => (Number.isInteger(q) ? String(q) : String(Math.round(q * 100) / 100));

export default function BidLines({ lines, info, how, defaultOpen, extra }: {
  lines: Line[]; info: Record<string, LineInfo> | null | undefined; how?: string | null;
  /** Open these kinds to begin with — the plan is the one worth reading. */
  defaultOpen?: string[];
  /** Something to draw under a line — the winter choice (WinterLinePick). Null for nothing. */
  extra?: (li: Line) => React.ReactNode;
}) {
  const [open, setOpen] = useState<Set<number>>(() => new Set(lines.filter(l => (defaultOpen || []).includes(info?.[l.id]?.kind || '')).map(l => l.id)));
  const toggle = (id: number) => setOpen(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  return (
    <ul className="divide-y divide-gray-100">
      {lines.map(li => {
        const about = info?.[li.id] || null;
        const isOpen = open.has(li.id);
        const row = (
          <span className="grid grid-cols-[1fr_auto] gap-x-3 items-baseline w-full">
            <span className="text-[15px] text-gray-800 text-left">
              {li.description}
              {li.rhythm_words && <span className="block text-xs text-gray-600">{li.rhythm_words}</span>}
              {Number(li.quantity) !== 1 && <span className="block text-xs text-gray-600 tabular-nums">{fmtQty(Number(li.quantity))} × {money(li.unit_price)}</span>}
            </span>
            <span className="text-right text-[15px] font-semibold text-gray-900 tabular-nums whitespace-nowrap">{money(li.total)}{per(li) && <span className="text-xs font-normal text-gray-500">{per(li)}</span>}</span>
          </span>
        );
        const under = extra ? extra(li) : null;
        if (!about) return <li key={li.id} className="py-3">{row}{under}</li>;
        return (
          <li key={li.id} className="py-3">
            <button type="button" onClick={() => toggle(li.id)} aria-expanded={isOpen} className="w-full text-left">
              {row}
              <span className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-brand-700">
                {isOpen ? 'Hide the details' : about.kind === 'plan' ? 'See your lawn’s whole year' : about.kind === 'fee' || about.kind === 'travel' ? 'What this is' : 'What you get'}
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              </span>
            </button>
            {isOpen && (
              <div className="mt-3 rounded-2xl border border-gray-200 bg-gray-50/70 p-4">
                <p className="text-[15px] font-semibold text-gray-900">{about.title}</p>
                {about.lead && <p className="text-sm text-gray-600 mt-1 leading-relaxed">{about.lead}</p>}
                {about.points && about.points.length > 0 && (
                  <ul className="mt-3 space-y-1.5">
                    {about.points.map(p => (
                      <li key={p} className="flex items-start gap-2 text-sm text-gray-700"><Check className="h-4 w-4 mt-0.5 text-emerald-600 flex-shrink-0" />{p}</li>
                    ))}
                  </ul>
                )}
                {about.plan && <div className="mt-4"><BidPlanYear plan={about.plan} how={how} mowed={about.mowed !== false} /></div>}
              </div>
            )}
            {under}
          </li>
        );
      })}
    </ul>
  );
}
